import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiClient } from "services/Api";

const SUBAGENT_OPTIONS = [
  "company_retriever",
  "sector_retriever",
  "market_trade_retriever",
  "market_profile_retriever",
  "appendix_retriever",
];

const DEFAULT_FIELD_REQUIREMENTS = {
  field_id: "",
  target: "",
  must_include: [],
  must_not_include: [],
  reserved_topics_for_other_fields: [],
};

const DEFAULT_RETRIEVE_INPUT = {};

const prettyJson = (value) => JSON.stringify(value ?? {}, null, 2);

const StreamPilotSubagentPlayground = () => {
  const { id } = useParams();
  const applicationId = useMemo(() => Number(id), [id]);

  const [agentName, setAgentName] = useState("company_retriever");
  const [fieldRequirementsJson, setFieldRequirementsJson] = useState(
    prettyJson(DEFAULT_FIELD_REQUIREMENTS),
  );
  const [retrieveAgentInputJson, setRetrieveAgentInputJson] = useState(
    prettyJson(DEFAULT_RETRIEVE_INPUT),
  );
  const [memoryFiles, setMemoryFiles] = useState([]);
  const [vectorStoreId, setVectorStoreId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taskId, setTaskId] = useState("");
  const [taskStatus, setTaskStatus] = useState("");

  const isAppendixRetriever = agentName === "appendix_retriever";

  const loadMemoryFiles = async () => {
    const response = await apiClient.get(
      `/stream-pilot/application/${applicationId}/memory/files/`,
    );
    setMemoryFiles(response.data?.files || []);
    setVectorStoreId(response.data?.vector_store_id || "");
  };

  useEffect(() => {
    if (!applicationId) return;
    loadMemoryFiles();
  }, [applicationId]);

  useEffect(() => {
    if (!taskId) return undefined;

    let cancelled = false;
    const pollTask = async () => {
      try {
        const response = await apiClient.get(
          `/stream-pilot/application/${applicationId}/subagents/test/${taskId}/`,
        );
        if (cancelled) return;
        if (response.status === 202) {
          setTaskStatus(response.data?.status || "PENDING");
          return;
        }
        setTaskStatus(response.data?.status || "SUCCESS");
        setResult(response.data?.result || null);
        setIsSubmitting(false);
        setTaskId("");
      } catch (requestError) {
        if (cancelled) return;
        const detail =
          requestError?.response?.data?.error ||
          requestError?.response?.data?.detail ||
          requestError?.response?.data ||
          requestError.message;
        setError(typeof detail === "string" ? detail : prettyJson(detail));
        setTaskStatus(requestError?.response?.data?.status || "FAILURE");
        setIsSubmitting(false);
        setTaskId("");
      }
    };

    pollTask();
    const timer = setInterval(pollTask, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [applicationId, taskId]);

  const runPreview = async () => {
    setIsSubmitting(true);
    setError("");
    setResult(null);
    setTaskStatus("PENDING");
    try {
      const fieldRequirements = JSON.parse(fieldRequirementsJson || "{}");
      const retrieveAgentInput = isAppendixRetriever
        ? {}
        : JSON.parse(retrieveAgentInputJson || "{}");

      const response = await apiClient.post(
        `/stream-pilot/application/${applicationId}/subagents/test/`,
        {
          agent_name: agentName,
          field_requirements: fieldRequirements,
          retrieve_agent_input: retrieveAgentInput,
        },
      );
      setTaskId(response.data?.task_id || "");
      setTaskStatus(response.data?.status || "PENDING");
    } catch (requestError) {
      const detail =
        requestError?.response?.data?.detail ||
        requestError?.response?.data ||
        requestError.message;
      setError(typeof detail === "string" ? detail : prettyJson(detail));
      setTaskStatus("FAILURE");
      setTaskId("");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h2>StreamPilot Subagent Playground</h2>
          <div className="text-muted">
            Executa subagentes sem criar `AgentJob` nem gravar `AgentRun`.
          </div>
        </div>
        <Link className="btn btn-outline-secondary" to={`/stream-pilot/${applicationId}`}>
          Voltar ao painel
        </Link>
      </div>

      <div className="row mt-4">
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Configuração</h5>

              <div className="mb-3">
                <label className="form-label">Subagente</label>
                <select
                  className="form-select"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                >
                  {SUBAGENT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Field Requirements (JSON)</label>
                <textarea
                  className="form-control font-monospace"
                  rows="14"
                  value={fieldRequirementsJson}
                  onChange={(e) => setFieldRequirementsJson(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Retrieve Agent Input (JSON)</label>
                <textarea
                  className="form-control font-monospace"
                  rows="10"
                  value={retrieveAgentInputJson}
                  onChange={(e) => setRetrieveAgentInputJson(e.target.value)}
                  disabled={isAppendixRetriever}
                />
                {isAppendixRetriever && (
                  <div className="form-text">
                    O `appendix_retriever` usa os anexos já carregados e ignora este
                    payload.
                  </div>
                )}
              </div>

              <button
                className="btn btn-primary"
                disabled={isSubmitting}
                onClick={runPreview}
              >
                {isSubmitting ? "Em worker..." : "Correr subagente"}
              </button>
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <h5 className="card-title">Contexto da aplicação</h5>
              <div>
                <strong>Application:</strong> {applicationId}
              </div>
              <div>
                <strong>Vector Store:</strong> {vectorStoreId || "não criado"}
              </div>
              <div>
                <strong>Anexos:</strong> {memoryFiles.length}
              </div>
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <h5 className="card-title">Anexos disponíveis</h5>
              {memoryFiles.length === 0 ? (
                <div className="text-muted">Sem anexos carregados.</div>
              ) : (
                <div className="list-group">
                  {memoryFiles.map((file) => (
                    <div key={file.id} className="list-group-item">
                      <div className="fw-semibold">{file.filename}</div>
                      <div className="small text-muted">
                        attachment_id={file.id} | openai_file_id={file.openai_file_id}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-7 mt-3 mt-lg-0">
          {error && (
            <div className="alert alert-danger">
              <pre className="mb-0 font-monospace small">{error}</pre>
            </div>
          )}

          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Resultado</h5>
              {!result ? (
                <div className="text-muted">
                  Corre um subagente para ver o input final, output e usage.
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <strong>Agent:</strong> {result.agent_name}
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong> {taskStatus || "SUCCESS"}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Input Payload</label>
                    <pre className="bg-light border rounded p-3 font-monospace small mb-0">
                      {prettyJson(result.input_payload)}
                    </pre>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Output Payload</label>
                    <pre className="bg-light border rounded p-3 font-monospace small mb-0">
                      {prettyJson(result.output_payload)}
                    </pre>
                  </div>
                  <div className="mb-0">
                    <label className="form-label">Usage</label>
                    <pre className="bg-light border rounded p-3 font-monospace small mb-0">
                      {prettyJson(result.usage)}
                    </pre>
                  </div>
                </>
              )}
              {!result && taskId && (
                <div className="alert alert-info mt-3 mb-0">
                  Worker a processar preview. Task ID: {taskId}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamPilotSubagentPlayground;
